/* eslint-disable max-lines-per-function */
import { ColumnDescriptionDc, ServiceInfoDc, TableInfoDc } from "@evergis/api";
import { changeProps, returnFound } from "find-and";
import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { getUsername } from "../../../../../../ducks/user";

export type TableStructureState = TableStructureTable[];

export type TableStructureTable = TableInfoDc & {
  name?: string;
  columns?: ColumnDescriptionDc[];
  selected?: boolean;
  layers?: TableStructureLayer[];
};

export type TableStructureLayer = ServiceInfoDc & {
  name?: string;
  selected?: boolean;
  filtered?: boolean;
};

export interface TableStructureControls {
  getStateByName: (name: string) => TableStructureTable | TableStructureLayer;
  updateSelected: (name: string, value: boolean, type: "table" | "layer", tableName: string) => void;
  updateFiltered: (name: string, value: boolean) => void;
  treeState: TableStructureState;
}

const useTableStructureControls = (
  state: TableStructureState,
  setFilteredLayers: (state: ServiceInfoDc[]) => void,
): TableStructureControls => {
  const currentUser: string = useSelector(getUsername);
  const [treeState, setTreeState] = useState<TableStructureState>(state);

  useEffect(() => {
    setTreeState(
      state &&
        state.map(table => ({
          ...table,
          selected: table.owner !== currentUser,
          layers: table.layers.map(layer => ({
            ...layer,
            selected: table.owner !== currentUser,
            filtered: table.owner !== currentUser,
          })),
        })),
    );
  }, [state]);

  useEffect(() => {
    setFilteredLayers(
      treeState
        ? (treeState.reduce((acc: ServiceInfoDc[], curr: TableStructureTable) => {
            const filtered: ServiceInfoDc[] = curr.layers
              ? (curr.layers.filter(item => item.filtered) as ServiceInfoDc[])
              : [];

            if (filtered.length) {
              return [...acc, ...filtered];
            }

            return acc;
          }, []) as ServiceInfoDc[])
        : [],
    );
  }, [treeState]);

  const updateSelected = useCallback(
    (name: string, value: boolean, type: "table" | "layer", tableName: string) => {
      const updatedState: TableStructureState = changeProps(treeState, { name }, { selected: value });
      const item: TableStructureTable | TableStructureLayer = returnFound(updatedState, {
        name,
      }) as TableStructureTable;

      const getDepState = () => {
        switch (type) {
          case "table":
            if (
              (item as TableStructureTable).layers?.length === 1 &&
              // FIXME: https://jr.everpoint.ru/browse/PUB-2350
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              !!(item as TableStructureTable).layers[0].configuration.condition
            ) {
              return changeProps(
                updatedState,
                { name: (item as TableStructureTable).layers[0].name },
                { filtered: value },
              );
            }

            return updatedState;
          case "layer":
            return !value
              ? changeProps(
                  changeProps(updatedState, { name: tableName }, { selected: false }),
                  { name },
                  { filtered: false },
                )
              : updatedState;
          default:
            return updatedState;
        }
      };

      setTreeState(getDepState());
    },
    [treeState],
  );

  const updateFiltered = useCallback(
    (name: string, value: boolean) => {
      setTreeState(changeProps(treeState, { name }, { filtered: value }));
    },
    [treeState],
  );

  const getStateByName = useCallback(
    (name: string) => {
      return returnFound(treeState, { name });
    },
    [treeState],
  );

  return {
    getStateByName,
    updateSelected,
    updateFiltered,
    treeState,
  };
};

export { useTableStructureControls };
